if (!customElements.get('sticky-atc-panel')) {
  class StickyAtcPanel extends HTMLElement {
    constructor() {
      super();

      this.productSection = this.closest('.cc-main-product');
      this.productInfo = this.productSection.querySelector('.product-info');
      this.productForm = this.productSection.querySelector('product-form');

      if ('IntersectionObserver' in window && 'MutationObserver' in window) {
        this.bindEvents();
      }

      this.initVariantSelect();
    }

    /**
     * Wires up the compact layout's per-option <select> elements so the
     * displayed price, savings badge and "Add to cart" button reflect the
     * chosen variant.
     */
    initVariantSelect() {
      this.optionSelects = [...this.querySelectorAll('[data-sticky-atc-option]')];
      this.idInput = this.querySelector('[data-sticky-atc-id]');
      if (!this.optionSelects.length || !this.idInput) return;

      this.variants = JSON.parse(this.querySelector('[data-sticky-atc-variants]').textContent);
      this.priceOptions = this.querySelectorAll('.sticky-atc-panel__price-option');
      this.addBtn = this.querySelector('[name="add"]');
      this.addBtnText = this.addBtn ? this.addBtn.querySelector('.sticky-atc-panel__cart-btn-text') : null;

      this.optionSelects.forEach((select) => {
        select.addEventListener('change', () => {
          if (this.dataset.hideUnavailable === 'true') {
            this.applyOptionVisibility(this.getSelectedOptions());
          }
          this.updateSelectedVariant();
          this.syncMainFormVariant();
        });
      });
      if (this.dataset.hideUnavailable === 'true') {
        const optionsEl = this.querySelector('[data-sticky-atc-option-values]');
        this.optionValues = optionsEl ? JSON.parse(optionsEl.textContent) : [];
        this.applyOptionVisibility(this.getSelectedOptions());
      }
      this.updateSelectedVariant();

      // Follow variant changes made in the main product form.
      this.boundHandleVariantChange = this.handleVariantChange.bind(this);
      this.productSection.addEventListener('on:variant:change', this.boundHandleVariantChange);
    }

    /**
     * Drops option values that cannot be bought with the earlier selected options.
     * Later options fall back to their first remaining value.
     * @param {string[]} selectedOptions - Option values in option order. Mutated.
     */
    applyOptionVisibility(selectedOptions) {
      if (!this.optionValues || !this.variants) return;

      this.optionSelects.forEach((select, index) => {
        const values = this.optionValues[index] || [];
        const allowed = values.filter((value) => this.variants.some((variant) => {
          if (!variant.available || variant.options[index] !== value) return false;
          for (let earlier = 0; earlier < index; earlier += 1) {
            if (variant.options[earlier] !== selectedOptions[earlier]) return false;
          }
          return true;
        }));
        if (!allowed.length) return;

        const next = allowed.includes(selectedOptions[index]) ? selectedOptions[index] : allowed[0];
        selectedOptions[index] = next;
        const existing = [...select.options].map((option) => option.value);
        if (existing.join('\n') !== allowed.join('\n')) {
          select.replaceChildren(...allowed.map((value) => {
            const option = document.createElement('option');
            option.value = value;
            option.textContent = value;
            return option;
          }));
        }
        select.value = next;
      });
    }

    /**
     * Gets the currently chosen option values, in option order.
     * @returns {string[]} Option values.
     */
    getSelectedOptions() {
      return this.optionSelects.map((select) => select.value);
    }

    /**
     * Finds the variant matching the currently chosen options.
     * @returns {?object} Matching variant, or null for an invalid combination.
     */
    getSelectedVariant() {
      const selected = this.getSelectedOptions();
      return this.variants.find(
        (variant) => variant.options.every((option, index) => option === selected[index])
      ) || null;
    }

    /**
     * Shows the price block for the selected variant and toggles the button.
     */
    updateSelectedVariant() {
      const variant = this.getSelectedVariant();
      const variantId = variant ? String(variant.id) : null;

      this.priceOptions.forEach((el) => {
        el.classList.toggle('hidden', el.dataset.variantId !== variantId);
      });

      // Disable the id input when no variant matches, so the form can never
      // be submitted with a stale variant id.
      this.idInput.disabled = !variant;
      if (variant) this.idInput.value = variant.id;

      if (this.addBtn) {
        let text = this.addBtn.dataset.addToCartText;
        if (!variant) {
          this.addBtn.disabled = true;
          text = theme.strings.noVariant;
        } else if (!variant.available) {
          this.addBtn.disabled = true;
          text = theme.strings.noStock;
        } else {
          this.addBtn.disabled = false;
        }
        if (this.addBtnText) this.addBtnText.textContent = text;
      }
    }

    /**
     * Mirrors a variant change from the main product form into the panel.
     * @param {CustomEvent} evt - 'on:variant:change' event from the variant picker.
     */
    handleVariantChange(evt) {
      const { variant } = evt.detail;
      if (!variant) return;

      if (this.dataset.hideUnavailable === 'true' && this.optionValues) {
        this.applyOptionVisibility(variant.options.slice());
      } else {
        variant.options.forEach((option, index) => {
          const select = this.optionSelects[index];
          if (select && select.querySelector(`option[value="${CSS.escape(option)}"]`)) {
            select.value = option;
          }
        });
      }
      this.updateSelectedVariant();
    }

    /**
     * Mirrors the panel's chosen options back to the main product form, so
     * both forms always agree on the selected options.
     */
    syncMainFormVariant() {
      const picker = this.productSection.querySelector('variant-picker');
      if (!picker) return;

      if (this.dataset.hideUnavailable === 'true') {
        const variant = this.getSelectedVariant();
        if (!variant || !variant.available) return;
        picker.dispatchEvent(new CustomEvent('change', {
          bubbles: true,
          detail: { variantId: String(variant.id), noFocus: true }
        }));
        return;
      }

      const optionValues = this.getSelectedOptions();
      let lastChangedInput = null;

      picker.querySelectorAll('.option-selector:not(.option-selector--custom)').forEach((selector, index) => {
        const value = optionValues[index];
        if (value == null) return;

        if (selector.dataset.selectorType === 'dropdown') {
          const option = selector.querySelector(`.custom-select__option[data-value="${CSS.escape(value)}"]`);
          if (option && option.getAttribute('aria-selected') !== 'true') {
            // selectOption dispatches a 'change' event the variant picker handles.
            option.closest('custom-select').selectOption(option);
          }
        } else {
          const input = selector.querySelector(`input[value="${CSS.escape(value)}"]`);
          if (input && !input.checked) {
            input.checked = true;
            lastChangedInput = input;
          }
        }
      });

      // Fire a single change so the picker only refetches once. noFocus avoids
      // the picker stealing focus back to the main form.
      if (lastChangedInput) {
        lastChangedInput.dispatchEvent(new CustomEvent('change', {
          bubbles: true,
          cancelable: false,
          detail: { noFocus: true }
        }));
      }
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', StickyAtcPanel.handleScroll);
      if (this.boundHandleVariantChange && this.productSection) {
        this.productSection.removeEventListener('on:variant:change', this.boundHandleVariantChange);
      }
    }

    bindEvents() {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.target === this.productForm && !theme.mediaMatches.md) {
            this.classList.toggle('sticky-atc-panel--out', entry.boundingClientRect.bottom > 0);
          } else if (entry.target === this.productInfo && theme.mediaMatches.md) {
            this.classList.toggle('sticky-atc-panel--out', entry.isIntersecting);
          }
        });
      });

      if (this.productForm) observer.observe(this.productForm);
      if (this.productInfo) observer.observe(this.productInfo);

      window.addEventListener('scroll', StickyAtcPanel.handleScroll);
    }

    /**
     * Watches for a scroll to the bottom of the page
     */
    static handleScroll() {
      document.body.classList.toggle(
        'scrolled-to-bottom',
        window.scrollY + window.innerHeight + 100 > document.body.scrollHeight
      );
    }
  }

  customElements.define('sticky-atc-panel', StickyAtcPanel);
}
